import React, { useEffect, useState } from "react";
import axios from "axios";
import "./App.css";
import { useNavigate } from "react-router-dom";

function SuperAdmin() {
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [csrfToken, setCsrfToken] = useState("");
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [adminSearch, setAdminSearch] = useState("");
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editAdminForm, setEditAdminForm] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [savingAdmin, setSavingAdmin] = useState(false);
  const navigate = useNavigate();

  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    (typeof err?.response?.data === "string" ? err.response.data : "") ||
    err?.message ||
    fallback;

  const loadAdmins = async () => {
    setLoadingAdmins(true);
    setError("");

    try {
      // Match the endpoint name used elsewhere in this app.
      const tokenRes = await axios.get("/api/getCSRF.php", {
        withCredentials: true,
      });

      const token = tokenRes.data?.csrf_token;
      if (!token) {
        throw new Error(
          "Impossible d’obtenir le jeton CSRF. Vérifiez getCSRF.php.",
        );
      }
      setCsrfToken(token);

      const res = await axios.get("/api/getAdmins.php", {
        withCredentials: true,
      });

      const payload = res.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.admins)
            ? payload.admins
            : null;

      if (!list) {
        throw new Error(
          payload?.message || "Réponse inattendue de getAdmins.php.",
        );
      }

      setAdmins(list);
    } catch (err) {
      console.error("Erreur lors du chargement des admins :", err);
      setAdmins([]);
      setError(
        "Impossible de charger la liste des administrateurs. " +
          getErrorMessage(
            err,
            "Vérifiez la connexion et vos droits Super Admin.",
          ),
      );
    } finally {
      setLoadingAdmins(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const addAdmin = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    const name = newAdmin.name.trim();
    const email = newAdmin.email.trim();
    const password = newAdmin.password;

    if (!name || !email || !password) {
      setError("Veuillez saisir le nom, l’adresse e-mail et le mot de passe.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Veuillez saisir une adresse e-mail valide.");
      return;
    }
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (!csrfToken) {
      setError("Jeton CSRF absent. Actualisez la page et réessayez.");
      return;
    }

    setAddingAdmin(true);
    try {
      const res = await axios.post(
        "/api/addAdmin.php",
        { name, email, password, csrf_token: csrfToken },
        { withCredentials: true },
      );

      if (res.data?.success !== true) {
        throw new Error(
          res.data?.message || res.data?.error || "L’API a refusé la création.",
        );
      }

      setNewAdmin({ name: "", email: "", password: "" });
      setMessage(res.data.message || "Administrateur ajouté.");
      await loadAdmins();
    } catch (err) {
      console.error("Erreur lors de l’ajout de l’admin :", err);
      setError(
        "Échec de l’ajout : " + getErrorMessage(err, "Erreur inconnue."),
      );
    } finally {
      setAddingAdmin(false);
    }
  };

  // Télécharger le journal d’activité. L’autorisation Super Admin doit aussi être vérifiée côté PHP.
  const downloadActivityHistory = async () => {
    setMessage("");
    setError("");
    try {
      const response = await axios.get("/api/exportActivityHistory.php", {
        withCredentials: true,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `historique_activites_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setMessage("Téléchargement de l’historique lancé.");
    } catch (err) {
      console.error("Erreur lors du téléchargement de l’historique :", err);
      setError(
        "Impossible de télécharger l’historique. " +
          getErrorMessage(
            err,
            "Vérifiez votre session Super Admin et l’API d’export.",
          ),
      );
    }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm("Supprimer cet admin ?")) return;

    setMessage("");
    setError("");

    if (!csrfToken) {
      setError("Jeton CSRF absent. Actualisez la page et réessayez.");
      return;
    }

    try {
      const res = await axios.post(
        "/api/deleteAdmin.php",
        { id, csrf_token: csrfToken },
        { withCredentials: true },
      );

      if (res.data?.success !== true) {
        throw new Error(
          res.data?.message ||
            res.data?.error ||
            "L’API a refusé la suppression.",
        );
      }

      setMessage(res.data.message || "Administrateur supprimé.");
      await loadAdmins();
    } catch (err) {
      console.error("Erreur lors de la suppression de l’admin :", err);
      setError(
        "Échec de la suppression : " + getErrorMessage(err, "Erreur inconnue."),
      );
    }
  };

  const openEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setEditAdminForm({
      name: getAdminDisplayName(admin),
      email: String(admin.email || ""),
      password: "",
    });
    setMessage("");
    setError("");
  };

  const saveAdminChanges = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!editingAdmin?.id) {
      setError("Administrateur introuvable.");
      return;
    }
    const name = editAdminForm.name.trim();
    const email = editAdminForm.email.trim().toLowerCase();
    const password = editAdminForm.password;

    if (!name || !email) {
      setError("Le nom et l’adresse e-mail sont obligatoires.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Veuillez saisir une adresse e-mail valide.");
      return;
    }
    if (password && password.length < 8) {
      setError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (!csrfToken) {
      setError("Jeton CSRF absent. Actualisez la page et réessayez.");
      return;
    }

    setSavingAdmin(true);
    try {
      const res = await axios.post(
        "/api/updateAdmin.php",
        { id: editingAdmin.id, name, email, password, csrf_token: csrfToken },
        { withCredentials: true },
      );
      if (res.data?.success !== true) {
        throw new Error(
          res.data?.message || res.data?.error || "La modification a échoué.",
        );
      }
      setEditingAdmin(null);
      setEditAdminForm({ name: "", email: "", password: "" });
      setMessage(
        res.data.message || "Informations de l’administrateur mises à jour.",
      );
      await loadAdmins();
    } catch (err) {
      console.error("Erreur lors de la modification de l’admin :", err);
      setError(
        "Échec de la modification : " +
          getErrorMessage(err, "Erreur inconnue."),
      );
    } finally {
      setSavingAdmin(false);
    }
  };

  // Accept common name-field formats returned by getAdmins.php.
  const getAdminDisplayName = (admin) => {
    const fullName =
      admin.name ||
      admin.full_name ||
      admin.fullName ||
      [admin.prenom, admin.nom].filter(Boolean).join(" ") ||
      [admin.first_name, admin.last_name].filter(Boolean).join(" ") ||
      [admin.nom, admin.prenom].filter(Boolean).join(" ");
    return String(fullName || "").trim();
  };

  const filteredAdmins = admins.filter((admin) => {
    const query = adminSearch.trim().toLowerCase();
    return (
      String(admin.email || "")
        .toLowerCase()
        .includes(query) ||
      getAdminDisplayName(admin).toLowerCase().includes(query) ||
      String(admin.role || "admin")
        .toLowerCase()
        .replaceAll("_", " ")
        .includes(query)
    );
  });

  return (
    <div className="super-admin-page">
      <style>{`
        .super-admin-page {
          --sa-ink: #14213d; --sa-muted: #7887a3; --sa-orange: #ff6900;
          min-height: 100vh; padding: 22px clamp(16px, 2.3vw, 38px) 0;
          color: var(--sa-ink); background: #f5f8fc; box-sizing: border-box;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .super-admin-page * { box-sizing: border-box; }
        .sa-shell { width: 100%; max-width: 1680px; margin: 0 auto; }
        .sa-topline { display: grid; grid-template-columns: 1fr auto 1fr; align-items: start; gap: 18px; margin-bottom: 26px; }
        .sa-back { justify-self: start; display: inline-flex; align-items: center; gap: 8px; border: 0; background: transparent; color: #40516f; border-radius: 9px; padding: 10px 2px; font-weight: 650; cursor: pointer; }
        .sa-back:hover { color: var(--sa-orange); }
        .sa-heading { text-align: center; }
        .sa-heading-title { display: flex; align-items: center; justify-content: center; gap: 13px; }
        .sa-crown { color: var(--sa-orange); font-size: 30px; line-height: 1; }
        .sa-heading h1 { margin: 0; font-size: clamp(24px, 2.2vw, 34px); line-height: 1.2; letter-spacing: -.7px; color: #101d38; font-weight: 800; }
        .sa-heading p { margin: 8px 0 0; color: #7b89a3; font-size: 15px; font-weight: 550; }
        .sa-top-spacer { min-width: 1px; }
        .sa-overview { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)) minmax(280px, 1.08fr); gap: 15px; margin-bottom: 28px; align-items: stretch; }
        .sa-stat { display: flex; align-items: center; gap: 17px; min-width: 0; padding: 18px 20px; min-height: 118px; border: 1px solid #e7edf5; border-radius: 13px; background: #fff; box-shadow: 0 5px 18px rgba(24, 43, 76, .035); }
        .sa-stat-icon { display: grid; place-items: center; flex: 0 0 58px; width: 58px; height: 58px; border-radius: 50%; font-size: 26px; }
        .sa-stat-icon.orange { color: #f36b13; background: #fff0e5; }
        .sa-stat-icon.blue { color: #2878ed; background: #e8f1ff; }
        .sa-stat-icon.green { color: #16a05b; background: #e4f9ed; }
        .sa-stat-copy { min-width: 0; }
        .sa-stat-label { font-size: 14px; font-weight: 750; color: #24334f; }
        .sa-stat-value { margin-top: 6px; font-size: 23px; font-weight: 800; letter-spacing: -.3px; color: #15213b; }
        .sa-stat-value.green-text { color: #1fa45d; font-size: 20px; }
        .sa-stat-hint { margin-top: 4px; color: #8290aa; font-size: 12px; line-height: 1.35; }
        .sa-export-wrap { display: flex; align-items: flex-start; justify-content: stretch; }
        .sa-export { width: 100%; min-height: 58px; display: inline-flex; align-items: center; justify-content: center; gap: 11px; border: 1.5px solid var(--sa-orange); border-radius: 11px; padding: 13px 16px; color: var(--sa-orange); background: #fff; font-weight: 750; font-size: 14px; cursor: pointer; transition: .18s ease; }
        .sa-export:hover { color: #fff; background: var(--sa-orange); box-shadow: 0 6px 16px #ff690026; }
        .sa-notice { padding: 12px 15px; border-radius: 10px; margin: 0 0 16px; font-size: 14px; }
        .sa-success { color: #166534; background: #dcfce7; border: 1px solid #bbf7d0; }
        .sa-error { color: #991b1b; background: #fee2e2; border: 1px solid #fecaca; white-space: pre-wrap; }
        .sa-main-grid { display: grid; grid-template-columns: minmax(300px, .78fr) minmax(0, 1.72fr); gap: 20px; align-items: stretch; }
        .sa-panel { min-width: 0; background: #fff; border: 1px solid #e5ecf5; border-radius: 13px; box-shadow: 0 5px 20px rgba(24, 43, 76, .035); overflow: hidden; }
        .sa-panel-head { padding: 23px 24px 18px; display: flex; align-items: flex-start; justify-content: space-between; gap: 15px; }
        .sa-panel-titleline { display: flex; align-items: flex-start; gap: 13px; min-width: 0; }
        .sa-panel-icon { color: var(--sa-orange); font-size: 27px; line-height: 1.1; }
        .sa-panel-head h2 { margin: 0; font-size: 19px; line-height: 1.3; font-weight: 800; color: #14213d; }
        .sa-panel-head p { margin: 7px 0 0; color: #8290aa; font-size: 13px; line-height: 1.5; }
        .sa-form { padding: 10px 25px 26px; display: flex; flex-direction: column; gap: 18px; }
        .sa-field { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
        .sa-field label { color: #263550; font-size: 13px; font-weight: 700; }
        .sa-input-wrap { position: relative; }
        .sa-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #71809a; font-size: 17px; pointer-events: none; }
        .sa-field input { width: 100%; min-width: 0; height: 47px; border: 1px solid #d8e1ed; border-radius: 9px; background: #fff; color: #15213b; padding: 11px 13px 11px 42px; font: inherit; font-size: 13px; outline: none; transition: border-color .15s, box-shadow .15s; }
        .sa-field input::placeholder { color: #95a1b5; }
        .sa-field input:focus { border-color: #ff984f; box-shadow: 0 0 0 3px #ff690015; }
        .sa-help { margin-top: -11px; color: #8290aa; font-size: 12px; }
        .sa-add { min-height: 47px; width: 100%; margin-top: 2px; padding: 12px 18px; border: 0; border-radius: 9px; background: linear-gradient(100deg, #ff6900, #ff780b); color: white; font-weight: 750; cursor: pointer; box-shadow: 0 5px 12px #ff69001c; transition: .18s ease; }
        .sa-add:hover:not(:disabled) { filter: brightness(.97); transform: translateY(-1px); }
        .sa-add:disabled { opacity: .6; cursor: not-allowed; }
        .sa-list-panel { display: flex; flex-direction: column; }
        .sa-list-head { align-items: center; flex-wrap: wrap; }
        .sa-search { position: relative; width: min(100%, 370px); flex: 0 1 370px; }
        .sa-search span { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #7887a3; font-size: 20px; }
        .sa-search input { width: 100%; height: 45px; padding: 10px 13px 10px 42px; border: 1px solid #d9e2ee; border-radius: 9px; outline: none; font: inherit; font-size: 13px; color: #14213d; background: #fff; }
        .sa-search input:focus { border-color: #ff984f; box-shadow: 0 0 0 3px #ff690015; }
        .sa-table-wrap { width: 100%; overflow-x: auto; padding: 0 24px 22px; }
        .sa-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; text-align: left; }
        .sa-table thead th { padding: 14px 15px; background: #f3f6fa; color: #34445f; font-weight: 750; white-space: nowrap; border-top: 1px solid #edf1f6; border-bottom: 1px solid #e5ebf3; }
        .sa-table thead th:first-child { border-top-left-radius: 9px; }
        .sa-table thead th:last-child { border-top-right-radius: 9px; text-align: center; }
        .sa-table tbody td { padding: 10px 15px; border-bottom: 1px solid #e9eef5; color: #192844; vertical-align: middle; }
        .sa-table tbody tr:last-child td { border-bottom: 0; }
        .sa-table tbody tr:hover { background: #fbfcfe; }
        .sa-table .sa-index { width: 58px; color: #8190aa; }
        .sa-table .sa-email-cell { overflow-wrap: anywhere; font-weight: 600; }
        .sa-role { display: inline-flex; align-items: center; border-radius: 8px; padding: 7px 12px; background: #fff0e7; color: #e85e08; font-size: 12px; font-weight: 700; text-transform: capitalize; white-space: nowrap; }
        .sa-action-cell { text-align: center; width: 100px; }
        .sa-delete { width: 42px; height: 39px; display: inline-grid; place-items: center; border: 1px solid #ffe0e0; border-radius: 9px; background: #fff0f0; color: #e32626; cursor: pointer; font-size: 18px; transition: .15s ease; }
        .sa-delete:hover { background: #ffe0e0; border-color: #ffcaca; }
        .sa-empty, .sa-loading { padding: 28px 18px; text-align: center; color: #8290aa; font-size: 14px; }
        .sa-footer { display: flex; align-items: center; gap: 12px; margin: 26px calc(-1 * clamp(16px, 2.3vw, 38px)) 0; padding: 18px clamp(16px, 2.3vw, 38px); border-top: 1px solid #e5ecf5; background: rgba(255,255,255,.86); color: #8290aa; font-size: 12px; font-weight: 600; }
        .sa-footer-sep { color: #c1cad8; }
        .sa-edit { width: 42px; height: 39px; display: inline-grid; place-items: center; margin-right: 7px; border: 1px solid #dbe8ff; border-radius: 9px; background: #eef5ff; color: #2463c5; cursor: pointer; font-size: 17px; }
        .sa-edit:hover { background: #dfeaff; }
        .sa-modal-backdrop { position: fixed; inset: 0; z-index: 9999; display: grid; place-items: center; padding: 18px; background: rgba(15, 28, 49, .55); }
        .sa-modal { width: min(100%, 520px); max-height: 90vh; overflow-y: auto; padding: 24px; border-radius: 16px; background: #fff; box-shadow: 0 24px 80px rgba(0,0,0,.22); }
        .sa-modal h2 { margin: 0 0 6px; color: #14213d; font-size: 22px; }
        .sa-modal p { margin: 0 0 20px; color: #7887a3; font-size: 13px; }
        .sa-modal .sa-field { margin-bottom: 15px; }
        .sa-modal-actions { display: flex; gap: 10px; margin-top: 20px; }
        .sa-cancel { min-height: 45px; padding: 10px 16px; border: 1px solid #d9e2ee; border-radius: 9px; background: #fff; color: #34445f; font-weight: 700; cursor: pointer; }
        .sa-modal-actions .sa-add { margin-top: 0; }
        @media (max-width: 1150px) { .sa-overview { grid-template-columns: repeat(3, minmax(0, 1fr)); } .sa-export-wrap { grid-column: 1 / -1; } .sa-export { min-height: 48px; } .sa-main-grid { grid-template-columns: minmax(280px, .8fr) minmax(0, 1.4fr); } }
        @media (max-width: 850px) { .sa-topline { grid-template-columns: 1fr; } .sa-heading { grid-row: 1; } .sa-back { grid-row: 2; } .sa-top-spacer { display: none; } .sa-overview { grid-template-columns: 1fr 1fr; } .sa-main-grid { grid-template-columns: 1fr; } .sa-list-head { align-items: flex-start; } .sa-search { flex-basis: 100%; width: 100%; } }
        @media (max-width: 560px) { .super-admin-page { padding: 14px 12px 0; } .sa-overview { grid-template-columns: 1fr; gap: 10px; margin-bottom: 18px; } .sa-stat { min-height: 94px; padding: 15px; } .sa-panel-head { padding: 18px 16px 13px; } .sa-form { padding: 8px 16px 19px; } .sa-table-wrap { padding: 0 12px 14px; } .sa-table thead th, .sa-table tbody td { padding: 11px 10px; } .sa-heading h1 { font-size: 24px; } .sa-heading p { font-size: 13px; } .sa-footer { margin-left: -12px; margin-right: -12px; flex-wrap: wrap; } }
      `}</style>

      <div className="sa-shell">
        <header className="sa-topline">
          <button
            type="button"
            className="sa-back"
            onClick={() => navigate("/admin")}
          >
            ← Retour
          </button>
          <div className="sa-heading">
            <div className="sa-heading-title">
              <span className="sa-crown" aria-hidden="true">
                👑
              </span>
              <h1>Super Admin Panel</h1>
            </div>
            <p>
              Gérez les administrateurs et consultez les activités du système.
            </p>
          </div>
          <div className="sa-top-spacer" aria-hidden="true" />
        </header>

        {message && (
          <div role="status" className="sa-notice sa-success">
            {message}
          </div>
        )}
        {error && (
          <div role="alert" className="sa-notice sa-error">
            {error}
          </div>
        )}

        <section className="sa-overview" aria-label="Vue d'ensemble">
          <div className="sa-stat">
            <div className="sa-stat-icon orange" aria-hidden="true">
              ♙
            </div>
            <div className="sa-stat-copy">
              <div className="sa-stat-label">Administrateurs</div>
              <div className="sa-stat-value">{admins.length}</div>
              <div className="sa-stat-hint">Administrateurs enregistrés</div>
            </div>
          </div>
          <div className="sa-stat">
            <div className="sa-stat-icon blue" aria-hidden="true">
              ▤
            </div>
            <div className="sa-stat-copy">
              <div className="sa-stat-label">Journal d’activité</div>
              <div className="sa-stat-value green-text">Disponible</div>
              <div className="sa-stat-hint">
                Téléchargez l’historique des activités
              </div>
            </div>
          </div>
          <div className="sa-stat">
            <div className="sa-stat-icon green" aria-hidden="true">
              ♢
            </div>
            <div className="sa-stat-copy">
              <div className="sa-stat-label">Sécurité</div>
              <div className="sa-stat-value" style={{ fontSize: "19px" }}>
                Accès restreint
              </div>
              <div className="sa-stat-hint">
                Réservé aux super administrateurs
              </div>
            </div>
          </div>
          <div className="sa-export-wrap">
            <button
              type="button"
              className="sa-export"
              onClick={downloadActivityHistory}
            >
              <span aria-hidden="true" style={{ fontSize: "21px" }}>
                ⇩
              </span>{" "}
              Télécharger l’historique des activités (CSV)
            </button>
          </div>
        </section>

        <main className="sa-main-grid">
          <section className="sa-panel">
            <div className="sa-panel-head">
              <div className="sa-panel-titleline">
                <span className="sa-panel-icon" aria-hidden="true">
                  ♟
                </span>
                <div>
                  <h2>Ajouter un administrateur</h2>
                  <p>
                    Créez un nouvel accès avec un e-mail et un mot de passe.
                  </p>
                </div>
              </div>
            </div>
            <form className="sa-form" onSubmit={addAdmin}>
              <div className="sa-field">
                <label htmlFor="sa-admin-name">Nom complet</label>
                <div className="sa-input-wrap">
                  <span className="sa-input-icon" aria-hidden="true">
                    ♙
                  </span>
                  <input
                    id="sa-admin-name"
                    type="text"
                    autoComplete="name"
                    placeholder="Entrez le nom complet de l’administrateur"
                    value={newAdmin.name}
                    onChange={(e) =>
                      setNewAdmin({ ...newAdmin, name: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
              <div className="sa-field">
                <label htmlFor="sa-admin-email">Email</label>
                <div className="sa-input-wrap">
                  <span className="sa-input-icon" aria-hidden="true">
                    ✉
                  </span>
                  <input
                    id="sa-admin-email"
                    type="email"
                    autoComplete="email"
                    placeholder="Entrez l’adresse e-mail de l’administrateur"
                    value={newAdmin.email}
                    onChange={(e) =>
                      setNewAdmin({ ...newAdmin, email: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
              <div className="sa-field">
                <label htmlFor="sa-admin-password">Mot de passe</label>
                <div className="sa-input-wrap">
                  <span className="sa-input-icon" aria-hidden="true">
                    ♙
                  </span>
                  <input
                    id="sa-admin-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Entrez un mot de passe"
                    minLength={8}
                    value={newAdmin.password}
                    onChange={(e) =>
                      setNewAdmin({ ...newAdmin, password: e.target.value })
                    }
                    required
                  />
                </div>
                <span className="sa-help">8 caractères minimum</span>
              </div>
              <button
                type="submit"
                className="sa-add"
                disabled={addingAdmin || !csrfToken}
              >
                {addingAdmin ? "Ajout en cours…" : "＋  Ajouter"}
              </button>
            </form>
          </section>

          <section className="sa-panel sa-list-panel">
            <div className="sa-panel-head sa-list-head">
              <div className="sa-panel-titleline">
                <span className="sa-panel-icon" aria-hidden="true">
                  ♟♟
                </span>
                <div>
                  <h2>Liste des administrateurs</h2>
                  <p>Voici la liste de tous les administrateurs du système.</p>
                </div>
              </div>
              <label className="sa-search">
                <span aria-hidden="true">⌕</span>
                <input
                  type="search"
                  value={adminSearch}
                  onChange={(e) => setAdminSearch(e.target.value)}
                  placeholder="Rechercher un administrateur…"
                  aria-label="Rechercher un administrateur"
                />
              </label>
            </div>
            {loadingAdmins ? (
              <div className="sa-loading">Chargement des administrateurs…</div>
            ) : admins.length === 0 && !error ? (
              <div className="sa-empty">Aucun administrateur à afficher.</div>
            ) : filteredAdmins.length === 0 ? (
              <div className="sa-empty">
                Aucun administrateur ne correspond à cette recherche.
              </div>
            ) : (
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nom</th>
                      <th>E-mail</th>
                      <th>Rôle</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdmins.map((admin, index) => (
                      <tr key={admin.id}>
                        <td className="sa-index">{index + 1}</td>
                        <td className="sa-email-cell">
                          {getAdminDisplayName(admin) || "Nom non renseigné"}
                        </td>
                        <td className="sa-email-cell">{admin.email || "—"}</td>
                        <td>
                          <span className="sa-role">
                            {(admin.role || "admin").replaceAll("_", " ")}
                          </span>
                        </td>
                        <td className="sa-action-cell">
                          {admin.role !== "super_admin" && (
                            <>
                              <button
                                type="button"
                                className="sa-edit"
                                onClick={() => openEditAdmin(admin)}
                                aria-label={`Modifier ${admin.email}`}
                                title={`Modifier ${admin.email}`}
                              >
                                ✎
                              </button>
                              <button
                                type="button"
                                className="sa-delete"
                                onClick={() => deleteAdmin(admin.id)}
                                aria-label={`Supprimer ${admin.email}`}
                                title={`Supprimer ${admin.email}`}
                              >
                                ♜
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>

        {editingAdmin && (
          <div
            className="sa-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !savingAdmin)
                setEditingAdmin(null);
            }}
          >
            <section
              className="sa-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sa-edit-title"
            >
              <h2 id="sa-edit-title">Modifier l’administrateur</h2>
              <p>
                Modifiez le nom ou l’adresse e-mail. Laissez le mot de passe
                vide pour le conserver.
              </p>
              <form onSubmit={saveAdminChanges}>
                <div className="sa-field">
                  <label htmlFor="sa-edit-name">Nom complet</label>
                  <div className="sa-input-wrap">
                    <span className="sa-input-icon" aria-hidden="true">
                      ♙
                    </span>
                    <input
                      id="sa-edit-name"
                      type="text"
                      autoComplete="name"
                      value={editAdminForm.name}
                      onChange={(e) =>
                        setEditAdminForm({
                          ...editAdminForm,
                          name: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="sa-field">
                  <label htmlFor="sa-edit-email">E-mail</label>
                  <div className="sa-input-wrap">
                    <span className="sa-input-icon" aria-hidden="true">
                      ✉
                    </span>
                    <input
                      id="sa-edit-email"
                      type="email"
                      autoComplete="email"
                      value={editAdminForm.email}
                      onChange={(e) =>
                        setEditAdminForm({
                          ...editAdminForm,
                          email: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="sa-field">
                  <label htmlFor="sa-edit-password">
                    Nouveau mot de passe (facultatif)
                  </label>
                  <div className="sa-input-wrap">
                    <span className="sa-input-icon" aria-hidden="true">
                      ♙
                    </span>
                    <input
                      id="sa-edit-password"
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      placeholder="Laisser vide pour ne pas le modifier"
                      value={editAdminForm.password}
                      onChange={(e) =>
                        setEditAdminForm({
                          ...editAdminForm,
                          password: e.target.value,
                        })
                      }
                    />
                  </div>
                  <span className="sa-help">
                    Si renseigné : 8 caractères minimum.
                  </span>
                </div>
                <div className="sa-modal-actions">
                  <button
                    type="button"
                    className="sa-cancel"
                    onClick={() => setEditingAdmin(null)}
                    disabled={savingAdmin}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="sa-add"
                    disabled={savingAdmin || !csrfToken}
                  >
                    {savingAdmin
                      ? "Enregistrement…"
                      : "Enregistrer les modifications"}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        <footer className="sa-footer">
          <span aria-hidden="true">♙</span>
          <span>Administration sécurisée</span>
          <span className="sa-footer-sep">│</span>
          <span>Accès réservé aux super administrateurs</span>
        </footer>
      </div>
    </div>
  );
}

export default SuperAdmin;
